import React, { useCallback, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Stack,
    useTheme,
    Paper,
    InputAdornment,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
    Badge,
    Divider,
} from '@mui/material';
import {
    Search as SearchIcon,
    PowerSettingsNew as PowerIcon,
    PlayArrow as PlayIcon,
    Warning as WarningIcon,
    Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useDevices } from '../contexts/DevicesContext';
import { Device } from '../types/Device';
import DeviceCard from '../components/DeviceCard';
import NetworkReferencePanel from '../components/NetworkReferencePanel';
import EventsList from '../components/EventsList';
import { ReferenceBadgesProvider } from '../hooks/useReferenceBadges';
import { referencesApi } from '../services/referencesApi';

const REFERENCE_EVENTS_PAGE = 50;

// Filter types for the dashboard
type FilterType = 'all' | 'connected' | 'running' | 'issues';

const Dashboard: React.FC = () => {
    const theme = useTheme();
    const { devices } = useDevices();
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState<FilterType>('all');
    const loadReferenceEvents = useCallback(
        (before?: string) => referencesApi.getEvents({ limit: REFERENCE_EVENTS_PAGE, before, minSeverity: 'warning' }),
        []
    );

    // Dashboard statistics
    const totalDevices = devices.length;
    const connectedDevices = devices.filter(d => d.is_connected).length;
    const runningDevices = devices.filter(d => d.is_running).length;
    const issuesCount = devices.filter(d => {
        // Issues: disconnected or error state
        return !d.is_connected || d.status === 'error';
    }).length;

    // Handle filter change
    const handleFilterChange = (
        _event: React.MouseEvent<HTMLElement>,
        newFilter: FilterType | null,
    ) => {
        if (newFilter !== null) {
            setFilter(newFilter);
        }
    };

    // Filter and search devices
    const filteredDevices = devices.filter(device => {
        // Filter logic
        if (filter === 'connected' && !device.is_connected) return false;
        if (filter === 'running' && !device.is_running) return false;
        if (filter === 'issues') {
            // Only show devices with issues (disconnected or error)
            if (device.is_connected && device.status !== 'error') {
                return false;
            }
        }

        // Search logic
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                device.name.toLowerCase().includes(query) ||
                (device.hostname && device.hostname.toLowerCase().includes(query)) ||
                device.mqtt_id.toLowerCase().includes(query)
            );
        }
        
        return true;
    });

    return (
        <Box>
            {/* Dashboard Header */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    background: 'linear-gradient(90deg, rgba(30, 42, 58, 0.8) 0%, rgba(28, 39, 54, 0.9) 100%)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, md: 0 }
                }}>
                    <Box>
                        <Typography variant="h1" gutterBottom>
                            Device Dashboard
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Control and monitor all your connected devices
                        </Typography>
                    </Box>
                    
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Badge 
                            badgeContent={2} 
                            color="error"
                            sx={{ 
                                '& .MuiBadge-badge': {
                                    right: -3,
                                    top: 3,
                                }
                            }}
                        >
                            {/* Alerts button can be kept if it serves another purpose */}
                        </Badge>
                    </Stack>
                </Box>
                
                {/* Stats Row */}
                <Grid container spacing={2} sx={{ mt: 3 }}>
                    <Grid item xs={6} sm={3}>
                        <Paper
                            sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                bgcolor: 'background.paper',
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="h3" color="primary.main" sx={{ fontWeight: 700 }}>
                                {totalDevices}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Devices
                            </Typography>
                        </Paper>
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                        <Paper
                            sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                bgcolor: 'background.paper',
                                borderRadius: 2,
                            }}
                        >
                            <Typography 
                                variant="h3" 
                                color="success.main" 
                                sx={{ fontWeight: 700 }}
                            >
                                {connectedDevices}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Connected
                            </Typography>
                        </Paper>
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                        <Paper
                            sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                bgcolor: 'background.paper',
                                borderRadius: 2,
                            }}
                        >
                            <Typography 
                                variant="h3" 
                                color="info.main" 
                                sx={{ fontWeight: 700 }}
                            >
                                {runningDevices}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Running
                            </Typography>
                        </Paper>
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                        <Paper
                            sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                bgcolor: 'background.paper',
                                borderRadius: 2,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::after': issuesCount > 0 ? {
                                    content: '""',
                                    position: 'absolute',
                                    width: '100%',
                                    height: '2px',
                                    bottom: 0,
                                    left: 0,
                                    backgroundColor: theme.palette.error.main,
                                } : {},
                            }}
                        >
                            <Typography 
                                variant="h3" 
                                color={issuesCount > 0 ? "error.main" : "text.secondary"} 
                                sx={{ 
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {issuesCount}
                                {issuesCount > 0 && (
                                    <WarningIcon fontSize="small" sx={{ ml: 1 }} />
                                )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Issues
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>

            {/* Controls: Search and Filter */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <TextField
                    variant="outlined"
                    placeholder="Search devices..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: '24px', backgroundColor: theme.palette.background.paper }
                    }}
                    sx={{ flexGrow: 1, minWidth: '250px' }}
                />
                <ToggleButtonGroup
                    value={filter}
                    exclusive
                    onChange={handleFilterChange}
                    aria-label="Device filter"
                    sx={{ 
                        backgroundColor: theme.palette.background.paper, 
                        borderRadius: '24px', 
                        border: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <ToggleButton value="all" aria-label="all devices">All</ToggleButton>
                    <ToggleButton value="connected" aria-label="connected devices">Connected</ToggleButton>
                    <ToggleButton value="running" aria-label="running devices">Running</ToggleButton>
                    <ToggleButton value="issues" aria-label="devices with issues">Issues</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Results count */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {filteredDevices.length} of {devices.length} devices
                    {filter !== 'all' && (
                        <Chip 
                            label={`Filter: ${filter}`} 
                            size="small" 
                            onDelete={() => setFilter('all')} 
                            sx={{ ml: 1 }}
                        />
                    )}
                    {searchQuery && (
                        <Chip 
                            label={`Search: ${searchQuery}`} 
                            size="small" 
                            onDelete={() => setSearchQuery('')} 
                            sx={{ ml: 1 }}
                        />
                    )}
                </Typography>
            </Box>

            {/* Network reference: primary, backups, active L0, manual handover */}
            <NetworkReferencePanel />

            {/* Device Grid: one badges call shared by every card */}
            <ReferenceBadgesProvider>
                <Grid container spacing={3}>
                    {filteredDevices.map(device => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={device.id}>
                            <DeviceCard device={device} />
                        </Grid>
                    ))}

                    {filteredDevices.length === 0 && (
                        <Grid item xs={12}>
                            <Paper
                                sx={{
                                    p: 4,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    borderRadius: 3,
                                }}
                            >
                                <Typography variant="h6" gutterBottom>
                                    No devices found
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {filter !== 'all' || searchQuery 
                                        ? 'Try changing your filters or search query'
                                        : 'Add a device to get started'}
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </ReferenceBadgesProvider>

            {/* Reference events (warning and critical) */}
            <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Reference events
                </Typography>
                <EventsList load={loadReferenceEvents} pageSize={REFERENCE_EVENTS_PAGE} />
            </Paper>
        </Box>
    );
};

export default Dashboard; 